'use client'

import type { EventPayloadType } from '@maestro/data-layer'
import type { Subscription } from 'rxjs'

import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { createLogger } from '@maestro/core/logger'
import { EventType } from '@maestro/data-layer'
import { getGoogleAnalyticsConfig } from '@maestro/google-analytics/config'
import { GoogleAnalyticsConverter } from '@maestro/google-analytics/google-analytics-converter'
import { usePageViewData } from '@maestro/google-analytics/hooks/use-page-view-data'
import {
  GA_EVENT_MAPPINGS,
  GAeventName,
  type GAeventSubscriberProps,
  type GAmoduleConfig,
} from '@maestro/google-analytics/types'
import { hashUserData } from '@maestro/google-analytics/utils/hash-user-data'
import { resolveGaPageViewParams } from '@maestro/google-analytics/utils/page-type.utils'

const log = createLogger('maestro:google-analytics')

export const GoogleAnalyticsProvider = (props: GAeventSubscriberProps) => {
  const { children, converter: converterProp, pageTypeMapping } = props
  const { subscribeTo } = useEventBusContext()
  const subscriptionRef = useRef<Subscription | undefined>(undefined)
  const isSubscribedRef = useRef(false)
  const pageViewPayloadDataRef = useRef(usePageViewData())
  const configRef = useRef<GAmoduleConfig | null>(null)

  const currentPageViewData = usePageViewData()
  pageViewPayloadDataRef.current = currentPageViewData

  // Get configuration (only once)
  if (!configRef.current) {
    try {
      configRef.current = getGoogleAnalyticsConfig()

      if (!configRef.current.isEnabled && configRef.current.isDebug) {
        log.debug('[GoogleAnalyticsProvider] GA is disabled')
      }
    } catch (error) {
      if (configRef.current?.isDebug) {
        log.error('[GoogleAnalyticsProvider] Configuration error:', error)
      }
    }
  }

  const config = configRef.current

  const converter = useMemo(() => {
    return (
      converterProp ?? new GoogleAnalyticsConverter(config?.isDebug ?? false)
    )
  }, [config?.isDebug, converterProp])

  const isEnabled = !!(config?.isEnabled && config?.gtmId)

  // Initialize GTM
  useEffect(() => {
    if (!config?.isEnabled && config?.isDebug) {
      log.debug('[GoogleAnalyticsProvider] GA is disabled')
      return
    }

    if (!config?.gtmId) {
      if (config?.isDebug) {
        log.debug('[GoogleAnalyticsProvider] GTM ID is missing')
      }
      return
    }

    if (document.querySelector('#gtm-head-script')) {
      if (config.isDebug) {
        log.debug('[GoogleAnalyticsProvider] GTM script already exists')
      }
      return
    }

    window.dataLayer = window.dataLayer || []

    // Inject consent script first
    const consentScript = document.createElement('script')
    consentScript.setAttribute('data-cookieconsent', 'ignore')
    consentScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      gtag("consent", "default", {
        ad_personalization: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        analytics_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
        wait_for_update: 500,
      });
      gtag("set", "ads_data_redaction", true);
      gtag("set", "url_passthrough", true);
    `

    document.head.insertBefore(consentScript, document.head.firstChild)
    if (config.isDebug) {
      log.debug('[GoogleAnalyticsProvider] Consent script injected')
    }

    // Inject GTM script
    const headScript = document.createElement('script')
    headScript.id = 'gtm-head-script'
    headScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl+'${config.gtmUrlParams ?? ''}';f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${config.gtmId}');`

    document.head.insertBefore(headScript, consentScript.nextSibling)
    if (config.isDebug) {
      log.debug('[GoogleAnalyticsProvider] GTM head script injected')
    }

    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.gtmId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`

    document.body.insertBefore(noscript, document.body.firstChild)
    if (config.isDebug) {
      log.debug('[GoogleAnalyticsProvider] GTM setup complete!')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackEvent = useCallback(
    (eventName: string, parameters?: Record<string, unknown>) => {
      if (!isEnabled || typeof window === 'undefined' || !window.dataLayer) {
        return
      }

      try {
        if (config?.isDebug) {
          log.debug(`[GA] Tracking event: ${eventName}`, parameters)
        }

        window.dataLayer.push({ ecommerce: null })

        window.dataLayer.push({
          event: eventName,
          ...parameters,
        })
      } catch (error) {
        if (config?.isDebug) {
          log.warn('[GA] Failed to track event:', error)
        }
      }
    },
    [config?.isDebug, isEnabled]
  )

  // Subscribe to events
  if (!isSubscribedRef.current && isEnabled) {
    GA_EVENT_MAPPINGS.forEach((mapping) => {
      const subscriberId =
        mapping.subscriberId ||
        `google_analytics_${mapping.eventType.toLowerCase()}_subscriber`

      subscriptionRef.current = subscribeTo<
        EventPayloadType[typeof mapping.eventType]
      >(mapping.eventType, subscriberId)?.subscribe(async (data) => {
        try {
          let convertedData
          if (mapping.gaEventName === GAeventName.PAGE_VIEW) {
            // Don't track event if slides-in opened
            if ((data as { isHashChanged?: boolean }).isHashChanged) {
              return
            }

            const pageViewData = data as EventPayloadType[EventType.PAGE_VIEW]
            const rawData = {
              ...pageViewPayloadDataRef.current,
              ...resolveGaPageViewParams(
                pageViewData.pageType,
                pageTypeMapping
              ),
              ...(pageViewData.searchResultType && {
                search_result_type: pageViewData.searchResultType,
              }),
            }
            convertedData = rawData.user_data
              ? {
                  ...rawData,
                  user_data: await hashUserData(
                    rawData.user_data as Record<string, unknown>
                  ),
                }
              : rawData
          } else {
            const rawConverted = converter.convertEventData<
              EventPayloadType[typeof mapping.eventType]
            >(mapping.eventType, data)
            convertedData = rawConverted.user_data
              ? {
                  ...rawConverted,
                  user_data: await hashUserData(
                    rawConverted.user_data as Record<string, unknown>
                  ),
                }
              : rawConverted
          }

          trackEvent(mapping.gaEventName, convertedData)
        } catch (error) {
          if (config?.isDebug) {
            log.error(
              `[GA] Error processing ${mapping.eventType} event:`,
              error
            )
          }
        }
      })
    })

    if (subscriptionRef.current) {
      isSubscribedRef.current = true
    }
  }

  useEffect(() => {
    return () => {
      subscriptionRef.current?.unsubscribe()
      isSubscribedRef.current = false
    }
  }, [])

  return children
}
